import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import { useTour } from '@reactour/tour';
import { useFormikContext } from 'formik';
import { useFormWarning } from 'hooks';
import { isEqual } from 'lodash';
import { useGetDatas, useGetViewDatas } from 'modules/account/settings/hooks';
import { Button } from 'ui/components/common';
import { Tabs } from 'ui/components/nav';
import { TOUR_SELECTOR } from 'ui/partials/Tour';
import { SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';
import { DATA_SOURCE, VIEWDATA_TYPE } from 'utils/types';

import LeadAddressForm from './LeadAddressForm';
import LeadCustomFields from './LeadCustomFields';
import LeadDetailsForm from './LeadDetailsForm';
import { LEAD_FORM } from './schema';

const TabItems = [
  { label: 'Name & Contact', value: 'details' },
  { label: 'Billing address', value: 'address' },
  { label: 'Custom Fields', value: 'custom-fields' },
];

const LeadForm = forwardRef<any, any>((_props, ref) => {
  const [selectedTab, setSelectedTab] = useState('details');
  const { isOpen } = useTour();

  const {
    initialValues,
    errors,
    values,
    setValues,
    handleSubmit,
    isSubmitting,
    submitForm,
    setErrors,
  } = useFormikContext<LEAD_FORM>();

  const { data: customFields } = useGetDatas({
    source: DATA_SOURCE.LEAD,
  });

  const { data: leadsCustomFields } = useGetViewDatas({
    type: VIEWDATA_TYPE.FORM,
    source: DATA_SOURCE.LEAD,
  });

  const dataIds = useMemo(() => {
    const storedDataIds = leadsCustomFields?.data?.[0]?.data;

    if (customFields?.data && storedDataIds) {
      return customFields?.data
        .filter((field) =>
          storedDataIds.map((id) => id.dataId).includes(field._id),
        )
        .map((field) => ({
          ...field,
          sequence:
            storedDataIds.find((id) => id.dataId === field._id)?.sequence ?? 0,
        }))
        .sort((a, b) => a.sequence - b.sequence);
    } else {
      return [];
    }
  }, [customFields?.data, leadsCustomFields?.data]);

  useEffect(() => {
    if (isOpen) {
      setValues({
        firstName: 'John',
        emailAddress: 'john.smith@leapify.com',
        lastName: 'Smith',
        companyId: '',
        companyName: '',
      });
    }
  }, [isOpen, setValues]);

  const { onBack } = useFormWarning({
    isDirty: !isEqual(initialValues, values),
    item: 'lead',
  });

  useImperativeHandle(ref, () => ({
    onConfirmModal: () => {
      submitForm();
    },
    onSetErrors: (formErrors: any) => {
      setErrors(formErrors);
    },
  }));

  const renderComponent = useMemo(() => {
    switch (selectedTab) {
      case 'details':
        return <LeadDetailsForm />;
      case 'address':
        return <LeadAddressForm />;
      case 'custom-fields':
        return <LeadCustomFields />;
      default:
        return <></>;
    }
  }, [selectedTab]);

  return (
    <form onSubmit={handleSubmit}>
      <div
        data-tut={TOUR_SELECTOR.TOUR.CONTACT.LEAD.FORM.FORM}
        className="divide-y divide-separator-primary"
      >
        <Tabs
          items={TabItems}
          onChange={(val) => setSelectedTab(val.value)}
          selectedTab={selectedTab}
          size={SIZE.Small}
          type="secondary"
        />
        {renderComponent}
      </div>
      <div
        className={cn(
          'flex justify-end gap-3',
          'px-2.5 pt-5 mt-5',
          'border-t-[0.5px] border-separator-primary',
        )}
      >
        <Button
          onClick={() => {
            onBack();
          }}
          label="CANCEL"
          size="sm"
          intent="tertiary"
          disabled={isSubmitting}
        />
        <Button
          data-tut={TOUR_SELECTOR.TOUR.CONTACT.LEAD.FORM.SAVE_BUTTON}
          type="submit"
          label="SAVE"
          size="sm"
          disabled={
            isSubmitting ||
            !!Object.keys(errors).length ||
            values.data?.some(
              (d) =>
                dataIds.find((dataId) => dataId._id === d.dataId) && !!d.error,
            )
          }
        />
      </div>
    </form>
  );
});

export default LeadForm;
